import {
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';

import {
  ApplicationConfig,
  importProvidersFrom,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  provideAppInitializer,
  inject,
  isDevMode,
} from '@angular/core';

import {
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';

import { provideRouter, withPreloading } from '@angular/router';
import { SelectivePreloadingStrategy } from './core/services/selective-preloading.strategy';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { routes } from './app.routes';
import { acceptLanguageHeaderInterceptor } from './interceptors/accept-language-header.interceptor';
import { baseUrlInterceptor } from './interceptors/base-url.interceptor';
import { LanguageService } from './core/services/language.service';
import { IconRegistryService } from './core/services/icon-registry.service';
import { initializeSession } from './features/session/session-initializer';
import { provideStore, Store } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideEffects } from '@ngrx/effects';

import { featureFlagsReducer } from './features/feature-management/store/reducers/feature-flags.reducer';
import { FeatureFlagsEffects } from './features/feature-management/store/effects/feature-flags.effects';
import { loadFeatureFlags } from './features/feature-management/store/actions/feature-flags.actions';
import { AppState } from './store';
import { SessionEffects } from './features/session/store/effects/session.effects';
import { sessionReducer } from './features/session/store/reducers/session.reducer';
import { initializeAnalytics } from './core/analytics/analytics.initializer';

export function initializeLanguage(): Promise<void> {
  const languageService = inject(LanguageService);
  return languageService.initializeApp();
}

export function initializeIcons(): void {
  const iconRegistryService = inject(IconRegistryService);
  iconRegistryService.initializeIcons();
}

export function initializeFeatureFlags(): void {
  const store = inject(Store<AppState>);
  store.dispatch(loadFeatureFlags());
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideClientHydration(withEventReplay()),
    provideHttpClient(
      withFetch(),
      withInterceptors([baseUrlInterceptor, acceptLanguageHeaderInterceptor]),
    ),

    provideEffects([
      FeatureFlagsEffects,
      SessionEffects,
    ]),
    provideStore({
      featureFlags: featureFlagsReducer,
      session: sessionReducer,
    }),
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
    }),
    provideRouter(routes, withPreloading(SelectivePreloadingStrategy)),
    provideZonelessChangeDetection(),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ),
    provideAppInitializer(initializeLanguage),
    provideAppInitializer(initializeIcons),
    provideAppInitializer(initializeFeatureFlags),
    provideAppInitializer(initializeSession),
    provideAppInitializer(initializeAnalytics),
  ],
};

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}
